type IconProps = { className?: string; filled?: boolean };

const base = "1.75";

export function IconCollection({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
    </svg>
  );
}

export function IconMap({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21Z" />
      <circle cx="12" cy="9.5" r="2.25" />
    </svg>
  );
}

export function IconGear({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3.25" />
      <path d="M19.4 13.5c.06-.5.06-1 0-1.5l1.6-1.2-1.5-2.6-1.9.6a7 7 0 0 0-1.3-.75L15.9 6h-3.8l-.4 1.95a7 7 0 0 0-1.3.75l-1.9-.6-1.5 2.6 1.6 1.2c-.06.5-.06 1 0 1.5l-1.6 1.2 1.5 2.6 1.9-.6c.4.3.85.55 1.3.75l.4 2.05h3.8l.4-1.95a7 7 0 0 0 1.3-.75l1.9.6 1.5-2.6-1.6-1.2Z" />
    </svg>
  );
}

/* Nav icons below are themed for a record-collecting app and swap to a bold
   filled variant when active, matching the Instagram-style tab bar pattern. */

export function IconVinyl({ className, filled }: IconProps) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" className={className}>
        <circle cx="12" cy="12" r="9.5" fill="currentColor" />
        <circle cx="12" cy="12" r="6.4" fill="none" stroke="var(--ios-bg)" strokeOpacity="0.55" strokeWidth="1" />
        <circle cx="12" cy="12" r="4.4" fill="none" stroke="var(--ios-bg)" strokeOpacity="0.55" strokeWidth="1" />
        <circle cx="12" cy="12" r="2.6" fill="var(--ios-bg)" />
        <circle cx="12" cy="12" r="0.9" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="12" cy="12" r="6.4" strokeWidth="1.1" opacity="0.55" />
      <circle cx="12" cy="12" r="4.4" strokeWidth="1.1" opacity="0.55" />
      <circle cx="12" cy="12" r="2.6" />
      <circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconBook({ className, filled }: IconProps) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" className={className}>
        <path
          d="M6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"
          fill="currentColor"
        />
        <path
          d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
          fill="none"
          stroke="var(--ios-bg)"
          strokeOpacity="0.7"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
    </svg>
  );
}

// Nav icon for the merged Artists & Authors page — an autograph: the one
// mark both singers and authors put on their work. A microphone only spoke
// to music, and a person bust reads as a generic profile.
export function IconArtists({ className, filled }: IconProps) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" className={className}>
        <path
          d="M3 13 C5.5 6 7.5 17 10 11 C11.7 7 13.5 16 15.5 10.5 C16.6 8 18.2 11.5 21 8.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="3" y="18" width="18" height="2.4" rx="1.2" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 13 C5.5 6 7.5 17 10 11 C11.7 7 13.5 16 15.5 10.5 C16.6 8 18.2 11.5 21 8.5" />
      <path d="M3.5 19.2 L20.5 19.2" />
    </svg>
  );
}

export function IconPinDrop({ className, filled }: IconProps) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" className={className}>
        <path
          d="M12 22s-7.5-6.55-7.5-12.15A7.5 7.5 0 0 1 19.5 9.85C19.5 15.45 12 22 12 22Z"
          fill="currentColor"
        />
        <circle cx="12" cy="9.85" r="2.9" fill="var(--ios-bg)" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s-7.5-6.55-7.5-12.15A7.5 7.5 0 0 1 19.5 9.85C19.5 15.45 12 22 12 22Z" />
      <circle cx="12" cy="9.85" r="2.9" />
    </svg>
  );
}

export function IconMixerSliders({ className, filled }: IconProps) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" className={className}>
        <path d="M5 4v16M12 4v16M19 4v16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <circle cx="5" cy="8.5" r="2.6" fill="currentColor" />
        <circle cx="12" cy="15.5" r="2.6" fill="currentColor" />
        <circle cx="19" cy="6.5" r="2.6" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path d="M5 4v16M12 4v16M19 4v16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="5" cy="8.5" r="2.4" fill="var(--ios-bg)" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="12" cy="15.5" r="2.4" fill="var(--ios-bg)" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="19" cy="6.5" r="2.4" fill="var(--ios-bg)" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

export function IconPlus({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconChevronRight({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function IconChevronLeft({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}

export function IconSignOut({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}

export function IconSearch({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

export function IconX({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function IconSort({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4v16M7 20l-3.5-3.5M7 20l3.5-3.5" />
      <path d="M17 20V4M17 4l3.5 3.5M17 4l-3.5 3.5" />
    </svg>
  );
}

export function IconPhoto({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="9" cy="9" r="1.75" />
      <path d="M21 15l-5-5-9 9" />
    </svg>
  );
}

export function IconEye({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.75-7 10-7 10 7 10 7-3.75 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function IconEyeOff({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l18 18" />
      <path d="M10.6 5.2c.45-.08.92-.12 1.4-.12 6.25 0 10 7 10 7a17.9 17.9 0 0 1-3.44 4.4M6.6 6.6C3.9 8.3 2 12 2 12s3.75 7 10 7c1.6 0 3-.4 4.2-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}

export function IconTrash({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-8 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" />
    </svg>
  );
}

export function IconPencil({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

export function IconPerson({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20c1.4-3.6 4.3-5.5 7.5-5.5s6.1 1.9 7.5 5.5" />
    </svg>
  );
}

export function IconGlobe({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 4 6 4 9s-1.5 6.4-4 9c-2.5-2.6-4-6-4-9s1.5-6.4 4-9Z" />
    </svg>
  );
}

export function IconLanguage({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5h9M8.5 3v2M6.5 5c0 4 2.2 6.7 5 8.5M10.5 5c-.8 3.8-3.3 6.8-7 9" />
      <path d="M13 21l4-9 4 9M14.5 18h5" />
    </svg>
  );
}

export function IconDownload({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
    </svg>
  );
}

export function IconUpload({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 15V3M7.5 7.5 12 3l4.5 4.5" />
      <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
    </svg>
  );
}

export function IconHelp({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.3a2.5 2.5 0 1 1 3.75 2.16c-.8.47-1.25.9-1.25 1.79" />
      <path d="M12 17.25h.01" />
    </svg>
  );
}

export function IconKey({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="3.5" />
      <path d="M10 13l8-8M15 8l2 2M18 5l2 2" />
    </svg>
  );
}

export function IconChartBar({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20h18M7 20v-5M12 20V8M17 20v-9" />
    </svg>
  );
}

export function IconDatabase({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="6" rx="7.5" ry="3" />
      <path d="M4.5 6v12c0 1.657 3.358 3 7.5 3s7.5-1.343 7.5-3V6" />
      <path d="M4.5 12c0 1.657 3.358 3 7.5 3s7.5-1.343 7.5-3" />
    </svg>
  );
}

export function IconLock({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function IconHistory({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v4h4" />
      <path d="M12 8v4l3 2" />
    </svg>
  );
}

export function IconBorrow({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="9" height="12" rx="1.5" />
      <path d="M16 9h5M18.5 6.5 21 9l-2.5 2.5" />
      <path d="M6.5 11.5h3M6.5 14.5h3" />
    </svg>
  );
}

export function IconPeople({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <path d="M2.5 19c1.2-3.2 3.6-4.8 6.5-4.8s5.3 1.6 6.5 4.8" />
      <circle cx="17" cy="8.5" r="2.25" />
      <path d="M15.8 14.4c2.3.3 4 1.8 4.9 4.6" />
    </svg>
  );
}

export function IconSun({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 2.5v2.5M12 19v2.5M4.4 4.4l1.8 1.8M17.8 17.8l1.8 1.8M2.5 12H5M19 12h2.5M4.4 19.6l1.8-1.8M17.8 6.2l1.8-1.8" />
    </svg>
  );
}

export function IconMoon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13.5A8 8 0 1 1 10.5 4a6.5 6.5 0 0 0 9.5 9.5Z" />
    </svg>
  );
}

export function IconAuto({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="8.75" fill="none" stroke="currentColor" strokeWidth={base} />
      <path d="M12 3.25a8.75 8.75 0 0 1 0 17.5Z" fill="currentColor" />
    </svg>
  );
}

export function IconDisplay({ className, filled }: IconProps) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" className={className}>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.9" />
        <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth={base} />
      <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" />
    </svg>
  );
}

export function IconBarcode({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5v14M8 5v14M11 5v14M15 5v14M18 5v14M21 5v14" strokeWidth="1.5" />
    </svg>
  );
}

// Instagram's glyph is a filled-corner rounded square with a ring and a dot.
// Drawn with the same 24-box and stroke weight as the rest of this set so it
// sits with them; the dot is filled because a 1.75-weight ring that small
// renders as mush.
export function IconInstagram({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="5.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconCheckCircle({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path d="M7.5 12.5l3 3 6-6.5" stroke="var(--ios-bg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function IconWarningTriangle({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={base} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.3 3.9 2.5 17.3A2 2 0 0 0 4.2 20h15.6a2 2 0 0 0 1.7-2.7L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

// Filled, unlike the stroked set above: at the 10-12px the tracklist renders
// them, a stroked triangle reads as a smudge. Solid shapes hold their form.
export function IconPlayFill({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M7.5 4.8c0-.9 1-1.5 1.8-1L19 10.3c.7.5.7 1.5 0 2L9.3 18.7c-.8.5-1.8-.1-1.8-1V4.8Z" />
    </svg>
  );
}

export function IconPauseFill({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <rect x="6.5" y="4.5" width="4" height="15" rx="1.3" />
      <rect x="13.5" y="4.5" width="4" height="15" rx="1.3" />
    </svg>
  );
}
